/**
 * Admin-editable product page overrides.
 *
 * Pure, client-safe. A published override document is merged on top of the
 * code defaults (PDP_CONTENT, pack tiers, gallery, SEO) so the product page
 * renders admin edits without redeploying. Empty / missing fields always fall
 * back to the code defaults — an override never blanks a section.
 */
import { getPDPContentForHandle, type PDPContent } from "@/lib/productContent";
import { PACK_PRICING, type PackQty, type SkuHandle } from "@/lib/checkout-pricing";
import { isComingSoon } from "@/lib/storeStatus";

export const EDITABLE_HANDLES: SkuHandle[] = [
  "mag-absorb-pro",
  "liver-biome-plus",
  "ova-balance",
];

export type PageImage = { src: string; alt?: string };

export type PackOverride = {
  qty: PackQty;
  /** Selling price in rupees. Also used by cart + checkout discounting. */
  price?: number;
  /** MRP in rupees. */
  mrp?: number;
  badge?: string;
  supply?: string;
  perUnitLabel?: string;
  perDay?: number;
};

export type ActiveCardOverride = {
  src?: string;
  name?: string;
  dose?: string;
  benefit?: string;
};

export type ActivesOverride = {
  eyebrow?: string;
  heading?: string;
  intro?: string;
  /** Full replacement list. Empty / missing keeps the built-in cards. */
  cards?: ActiveCardOverride[];
};

export type ProductPageOverride = {
  seo?: { title?: string; description?: string };
  h1Suffix?: string;
  tldr?: { what?: string; who?: string; benefit?: string };
  howItWorks?: string;
  quickFacts?: { label: string; value: string }[];
  comparison?: { metric: string; truwe: string; standard: string }[];
  faqs?: { q: string; a: string }[];
  images?: PageImage[];
  actives?: ActivesOverride;
  packs?: PackOverride[];
};


export const EMPTY_OVERRIDE: ProductPageOverride = {};

function str(a: string | undefined | null, fallback: string): string {
  const v = (a ?? "").trim();
  return v.length ? v : fallback;
}

function arr<T>(a: T[] | undefined | null, fallback: T[]): T[] {
  return Array.isArray(a) && a.length ? a : fallback;
}

/** Merge an override document onto the code PDP content. */
export function mergePdpContent(
  base: PDPContent,
  ov: ProductPageOverride | null | undefined,
): PDPContent {
  if (!ov) return base;
  return {
    h1Suffix: str(ov.h1Suffix, base.h1Suffix),
    tldr: {
      what: str(ov.tldr?.what, base.tldr.what),
      who: str(ov.tldr?.who, base.tldr.who),
      benefit: str(ov.tldr?.benefit, base.tldr.benefit),
    },
    quickFacts: arr(ov.quickFacts, base.quickFacts),
    howItWorks: str(ov.howItWorks, base.howItWorks),
    comparison: arr(ov.comparison, base.comparison),
    faqs: arr(ov.faqs, base.faqs),
  };
}

/** Keep Ova purchase guidance aligned in the visible FAQ and FAQ schema. */
function withOvaAvailabilityFaqs(faqs: PDPContent["faqs"]): PDPContent["faqs"] {
  const availabilityFaq = {
    q: "Where can I buy Ova Balance?",
    a: isComingSoon("ova-balance")
      ? "Available on truwe.in. Tap Pre-book now to reserve Ova Balance for free. No payment is collected now."
      : "Available on truwe.in. Choose your pack on this page to order.",
  };
  let found = false;
  const updated = faqs.map((faq) => {
    const question = faq.q.trim().toLowerCase().replace(/[?!.]+$/, "").replace(/\s+/g, " ");
    // Only product purchase/launch questions, not ingredient, format or lab-report availability.
    const availability = /^(?:is|when is|when will) (?:ova(?: balance)?|it|this (?:product|formula))(?: be)? (?:available|in stock|out of stock|coming soon|launching)(?: (?:now|online|in india|on truwe\.in))?$/.test(question)
      || /^(?:where|how|when) can i (?:buy|purchase|order|get) (?:ova(?: balance)?|it|this product)(?: (?:now|online|in india))?$/.test(question)
      || /^can i (?:buy|purchase|order|pre[- ]?book|pre[- ]?order|pre[- ]?reserve)(?: (?:ova(?: balance)?|it|this product))?(?: now)?$/.test(question)
      || /^is pre[- ]?booking (?:open|available)(?: now)?$/.test(question);
    if (!availability) return faq;
    found = true;
    return { ...faq, a: availabilityFaq.a };
  });
  if (!found) updated.push(availabilityFaq);
  return updated;
}

export function mergePdpForHandle(
  handle: string,
  ov: ProductPageOverride | null | undefined,
): PDPContent | null {
  const base = getPDPContentForHandle(handle);
  if (!base) return null;
  const merged = mergePdpContent(base, ov);
  if (handle !== "ova-balance") return merged;
  const availabilityFaqs = withOvaAvailabilityFaqs(merged.faqs);
  if (!ov) return { ...merged, faqs: availabilityFaqs };

  // Formula facts stay current even when published admin arrays predate D3.
  // Preserve unrelated editorial rows and all pricing/gallery overrides.
  const actives = base.quickFacts.find((fact) => fact.label === "Key actives");
  const formulaQuestions = new Set([
    "What is Ova Balance?",
    "Does Ova Balance contain vitamin D3?",
    "Vegan and vegetarian?",
  ]);
  const faqs = availabilityFaqs.map((faq) =>
    formulaQuestions.has(faq.q) ? (base.faqs.find((item) => item.q === faq.q) ?? faq) : faq,
  );
  const d3Faq = base.faqs.find((faq) => faq.q === "Does Ova Balance contain vitamin D3?");
  if (d3Faq && !faqs.some((faq) => faq.q === d3Faq.q)) faqs.push(d3Faq);
  const quickFacts = merged.quickFacts.map((fact) =>
    actives && fact.label.trim().toLowerCase() === "key actives" ? actives : fact,
  );
  if (actives && !quickFacts.some((fact) => fact.label === "Key actives")) quickFacts.push(actives);
  return { ...merged, quickFacts, faqs };
}

/** Gallery images: override wins when it has at least one entry. */
export function mergeGallery(
  base: { src: string; alt: string }[],
  ov: ProductPageOverride | null | undefined,
): { src: string; alt: string }[] {
  const imgs = (ov?.images ?? []).filter((i) => (i?.src ?? "").trim().length > 0);
  if (!imgs.length) return base;
  return imgs.map((i, idx) => ({
    src: i.src.trim(),
    alt: (i.alt ?? "").trim() || base[idx]?.alt || "TruWe product",
  }));
}

/** Pack price/MRP overrides for one SKU, keyed by pack qty. */
export function packPricingFromOverride(
  handle: string,
  ov: ProductPageOverride | null | undefined,
): Partial<Record<PackQty, { price: number; mrp: number }>> {
  const defaults = PACK_PRICING[handle as SkuHandle];
  if (!defaults) return {};
  const out: Partial<Record<PackQty, { price: number; mrp: number }>> = {};
  for (const p of ov?.packs ?? []) {
    const qty = Number(p?.qty) as PackQty;
    if (![1, 2, 3].includes(qty)) continue;
    const price = Number(p?.price);
    const mrp = Number(p?.mrp);
    if (!Number.isFinite(price) && !Number.isFinite(mrp)) continue;
    out[qty] = {
      price: Number.isFinite(price) && price > 0 ? Math.round(price) : defaults[qty].price,
      mrp: Number.isFinite(mrp) && mrp > 0 ? Math.round(mrp) : defaults[qty].mrp,
    };
  }
  return out;
}

/** Display-level pack fields (labels/badges) for the pack selector. */
export function packDisplayFromOverride(
  ov: ProductPageOverride | null | undefined,
): Partial<Record<PackQty, PackOverride>> {
  const out: Partial<Record<PackQty, PackOverride>> = {};
  for (const p of ov?.packs ?? []) {
    const qty = Number(p?.qty) as PackQty;
    if (![1, 2, 3].includes(qty)) continue;
    out[qty] = p;
  }
  return out;
}

/** Normalises whatever the admin form produced into a storable document. */
export function sanitizeOverride(input: unknown): ProductPageOverride {
  const o = (input ?? {}) as ProductPageOverride;
  const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const doc: ProductPageOverride = {};
  const seoTitle = text(o.seo?.title);
  const seoDesc = text(o.seo?.description);
  if (seoTitle || seoDesc) doc.seo = { title: seoTitle, description: seoDesc };
  if (text(o.h1Suffix)) doc.h1Suffix = text(o.h1Suffix);
  if (text(o.howItWorks)) doc.howItWorks = text(o.howItWorks);
  const what = text(o.tldr?.what);
  const who = text(o.tldr?.who);
  const benefit = text(o.tldr?.benefit);
  if (what || who || benefit) doc.tldr = { what, who, benefit };
  const qf = (o.quickFacts ?? []).filter((f) => text(f?.label) && text(f?.value));
  if (qf.length) doc.quickFacts = qf.map((f) => ({ label: text(f.label), value: text(f.value) }));
  const cmp = (o.comparison ?? []).filter((r) => text(r?.metric));
  if (cmp.length)
    doc.comparison = cmp.map((r) => ({
      metric: text(r.metric),
      truwe: text(r.truwe),
      standard: text(r.standard),
    }));
  const faqs = (o.faqs ?? []).filter((f) => text(f?.q) && text(f?.a));
  if (faqs.length) doc.faqs = faqs.map((f) => ({ q: text(f.q), a: text(f.a) }));
  const imgs = (o.images ?? []).filter((i) => text(i?.src));
  if (imgs.length) doc.images = imgs.map((i) => ({ src: text(i.src), alt: text(i.alt) }));
  const actives = sanitizeActives(o.actives);
  if (actives) doc.actives = actives;

  const packs = (o.packs ?? []).filter((p) => [1, 2, 3].includes(Number(p?.qty)));
  if (packs.length)
    doc.packs = packs.map((p) => ({
      qty: Number(p.qty) as PackQty,
      // Publishing synchronises every pack price with its catalog variant atomically.
      price:
        Number(p.price) > 0 ? Math.round(Number(p.price)) : undefined,
      mrp: Number(p.mrp) > 0 ? Math.round(Number(p.mrp)) : undefined,
      badge: text(p.badge) || undefined,
      supply: text(p.supply) || undefined,
      perUnitLabel: text(p.perUnitLabel) || undefined,
      perDay: Number(p.perDay) > 0 ? Math.round(Number(p.perDay)) : undefined,
    }));
  return doc;
}

/* ------------------------------------------------------------------ */
/* Clinically-studied actives (ingredient cards)                       */
/* ------------------------------------------------------------------ */

export type ActiveCardContent = {
  src: string;
  name: string;
  dose: string;
  benefit: string;
};

function sanitizeActives(input: unknown): ActivesOverride | undefined {
  const a = (input ?? {}) as ActivesOverride;
  const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const cards = (a.cards ?? [])
    .filter((c) => text(c?.src) || text(c?.name))
    .map((c) => ({
      src: text(c.src),
      name: text(c.name),
      dose: text(c.dose),
      benefit: text(c.benefit),
    }));
  const doc: ActivesOverride = {};
  if (text(a.eyebrow)) doc.eyebrow = text(a.eyebrow);
  if (text(a.heading)) doc.heading = text(a.heading);
  if (text(a.intro)) doc.intro = text(a.intro);
  if (cards.length) doc.cards = cards;
  return Object.keys(doc).length ? doc : undefined;
}

/**
 * Merge admin-edited ingredient cards on top of the code defaults.
 * A card only overrides the fields the admin actually filled in; an empty
 * override list keeps the built-in cards untouched.
 */
export function mergeActiveCards(
  base: ActiveCardContent[],
  ov: ActivesOverride | null | undefined,
): ActiveCardContent[] {
  const cards = (ov?.cards ?? []).filter((c) => (c?.src ?? "").trim() || (c?.name ?? "").trim());
  if (!cards.length) return base;
  return cards
    .map((c, idx) => {
      const b = base[idx];
      return {
        src: str(c.src, b?.src ?? ""),
        name: str(c.name, b?.name ?? ""),
        dose: str(c.dose, b?.dose ?? ""),
        benefit: str(c.benefit, b?.benefit ?? ""),
      };
    })
    .filter((c) => c.src.length > 0);
}

